import { useState, useEffect } from 'react';
import { TenantResolver } from '../../../core/tenant/TenantResolver';
import { getApiEndpoint } from '../../../core/config/apiConfig';

export interface User {
  email: string;
  firstName: string;
  lastName: string;
  project: string;
}

export const useSimpleAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check session storage on mount
  useEffect(() => {
    const storedUser = sessionStorage.getItem('auth_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Failed to parse stored user:', error);
        sessionStorage.removeItem('auth_user');
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (email: string): Promise<{ success: boolean; message: string }> => {
    try {
      setIsLoading(true);
      const projectName = TenantResolver.resolveTenant();

      const response = await fetch(getApiEndpoint('auth/validate-email'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, project: projectName }),
      });

      const data = await response.json();

      if (data.success && data.user) {
        setUser(data.user);
        sessionStorage.setItem('auth_user', JSON.stringify(data.user));
        return { success: true, message: 'Login successful' };
      } else {
        return { success: false, message: data.message || 'Authentication failed' };
      }
    } catch (error) {
      console.error('Authentication error:', error);
      return { success: false, message: 'Authentication failed' };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    sessionStorage.removeItem('auth_user');
  };

  return {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
  };
};